import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useApp } from '@/context/AppContext';
import { CheckCircle, Info, ChevronRight, Lightbulb, RotateCcw } from 'lucide-react';
import NasalIcon from '@/assets/nasal.svg?react';
import OronasalIcon from '@/assets/oronasal.svg?react';

export default function RespiracionResultadosStep() {
  const navigate = useNavigate();
  const { state } = useApp();
  const { noseScore, maskRecommendation, breathingSelfReport } = state.eleccion;

  // Handle header back button clicks
  useEffect(() => {
    const handleHeaderBack = () => {
      // If user came from NOSE questionnaire (breathingSelfReport is not 'bien'), go back to NOSE questionnaire
      // If user came directly from "bien" response, go back to initial breathing question
      if (breathingSelfReport !== 'bien') {
        // User completed NOSE questionnaire, navigate back to the questionnaire with NOSE flow active
        navigate('/eleccion/respiracion', { state: { showNoseQuestionnaire: true } });
      } else {
        // User answered "bien" and went directly to results, go back to initial breathing question
        navigate('/eleccion/respiracion');
      }
    };

    window.addEventListener('headerBackClick', handleHeaderBack);
    return () => window.removeEventListener('headerBackClick', handleHeaderBack);
  }, [navigate, breathingSelfReport]);

  const getScoreInterpretation = () => {
    if (breathingSelfReport === 'bien') {
      return {
        level: 'Excelente',
        color: 'bg-success/10 border-success/20 text-success',
        icon: <CheckCircle className="h-5 w-5 text-success" />,
        description: 'Tu respiración nasal funciona muy bien. No reportaste problemas significativos.',
        recommendation: 'Una mascarilla nasal será perfecta para ti, ofreciendo máxima comodidad y un sellado óptimo.'
      };
    }

    if (noseScore === null || noseScore === undefined) return null;

    if (noseScore < 25) {
      return {
        level: 'Obstrucción mínima',
        color: 'bg-white border-success text-success',
        icon: <CheckCircle className="h-5 w-5 text-success" />,
        description: 'Tienes muy pocos problemas de obstrucción nasal.',
        recommendation: 'Una mascarilla nasal será ideal para tu caso.'
      };
    } else if (noseScore < 50) {
      return {
        level: 'Obstrucción leve-moderada',
        color: 'bg-yellow-100 border-yellow-300 text-yellow-800',
        icon: <Info className="h-5 w-5 text-yellow-600" />,
        description: 'Experimentas algunos problemas de obstrucción nasal, pero aún puedes beneficiarte de una mascarilla nasal.',
        recommendation: 'Recomendamos una mascarilla nasal, pero mantente atento a tu comodidad durante el uso.'
      };
    } else {
      return {
        level: 'Obstrucción significativa',
        color: 'bg-orange-100 border-orange-300 text-orange-800',
        icon: <Info className="h-5 w-5 text-orange-600" />,
        description: 'Tienes problemas notables de obstrucción nasal que pueden afectar tu respiración durante el sueño.',
        recommendation: 'Una mascarilla oronasal te permitirá respirar por boca y nariz, asegurando una terapia efectiva.'
      };
    }
  };

  const interpretation = getScoreInterpretation();
  if (!interpretation) return null;

  const continuar = () => {
    navigate('/eleccion/medicion');
  };

  const redoSegment = () => {
    // Reset the segment data and go back to breathing question
    navigate('/eleccion/respiracion');
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-80px)]">
      <div className="flex-1 container px-4 py-8 pb-24">
        <div className="max-w-2xl mx-auto">
          {breathingSelfReport === 'bien' ? (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-primary mb-2">
                  ¡Excelente respiración!
                </h1>
              </div>
            </>
          ) : breathingSelfReport !== null ? (
            <>
              <div className="text-center mb-4">
                <h1 className="text-3xl font-bold text-primary">
                  Tus resultados:
                </h1>
              </div>

              {/* Score Display */}
              <div className={`rounded-lg p-4 mb-4 ${interpretation.color}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    {interpretation.level} ({`${noseScore}/100 puntos`})
                  </CardTitle>
                </div>
                <p className="text-md font-medium mb-3">
                  {interpretation.description}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-primary mb-2">
                  Aquí nuestra recomendación
                </h1>
              </div>
            </>
          )}

          {/* Recommendation */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-primary min-w-5 mr-2" />
                Recomendación: mascarilla {maskRecommendation}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Visual representation */}
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className={`p-3 rounded-lg border-2 relative ${
                  maskRecommendation === 'Nasal' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border bg-muted/30'
                }`}>
                  {/* Selection indicator */}
                  {maskRecommendation === 'Nasal' && (
                    <div className="absolute top-2 right-2">
                      <div className="w-5 h-5 rounded-full bg-primary border-primary flex items-center justify-center">
                        <CheckCircle className="h-3 w-3 text-primary-foreground" />
                      </div>
                    </div>
                  )}
                  <NasalIcon className="w-8 h-8 mx-auto mb-1" />
                  <div className="text-sm font-medium">Nasal</div>
                  <div className="text-sm text-muted-foreground">Solo nariz</div>
                </div>
                <div className={`p-3 rounded-lg border-2 relative ${
                  maskRecommendation === 'Oronasal' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border bg-muted/30'
                }`}>
                  {/* Selection indicator */}
                  {maskRecommendation === 'Oronasal' && (
                    <div className="absolute top-2 right-2">
                      <div className="w-5 h-5 rounded-full bg-primary border-primary flex items-center justify-center">
                        <CheckCircle className="h-3 w-3 text-primary-foreground" />
                      </div>
                    </div>
                  )}
                  <OronasalIcon className="w-8 h-8 mx-auto mb-1" />
                  <div className="text-sm font-medium">Oronasal</div>
                  <div className="text-sm text-muted-foreground">Boca y nariz</div>
                </div>
              </div>
              <p className="text-sm text-foreground mb-4 mt-4">
                {interpretation.recommendation}
              </p>
            </CardContent>
          </Card>

          {/* Continue Button */}
          <div className="space-y-3">
            <Button className="min-h-touch px-6 sm:px-8 gap-0 w-full text-lg" onClick={continuar}>
              <span className="">Continuar</span>
              <ChevronRight style={{ marginLeft: '2.5px', width: '1.25rem', height: '1.25rem', strokeWidth: '3px'}}/>
            </Button>
            
            <Button 
              variant="outline"
              className="w-full min-h-touch text-lg" 
              onClick={redoSegment}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Rehacer este segmento
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}